import { isProviderShellLinkTitle } from '../utils/linkPreviewPolicy'
import { decodeHtmlEntities } from '../utils/htmlEntities'
import { classifyRedditUrl } from '../utils/redditUrl'
import { removeKnownTrackingParameters } from '../utils/urlTracking'
import { createId } from '../utils/id'
import type { Reminder } from './reminder'
import type { ContentType, SyncState, UrlEntity } from './noteTypes'
export type { ContentType, LinkAnswer, LinkGist, SyncState, UrlEntity } from './noteTypes'
import {
  isPageKind,
  suggestedTagsForUrlCategories,
  urlContentCategory,
  type UrlContentCategory,
} from './urlCategory'

export type NoteStatus = 'inbox' | 'kept' | 'snoozed' | 'archived' | 'expired' | 'trash'
export const NOTE_SYNC_SCHEMA_VERSION = 2

export interface Note {
  id: string
  body: string
  manualTitle: string | null
  derivedTitle: string
  annotation: string
  userTags: string[]
  suggestedTags: string[]
  contentType: ContentType
  urls: UrlEntity[]
  suppressedUrlKeys?: string[]
  assetIds: string[]
  status: NoteStatus
  quickAccess: boolean
  quickAccessAt: number | null
  reminder: Reminder | null
  snoozedUntil: number | null
  expiresAt: number | null
  expiredAt: number | null
  trashedAt: number | null
  createdAt: number
  modifiedAt: number
  capturedAt: number
  originDeviceId: string
  lastModifiedDeviceId: string
  revisionId: string
  localSyncState: SyncState
}

export const GOOGLE_KEEP_ROLLBACK_DEVICE_PREFIX = 'import-rollback:'

export function isGoogleKeepRollbackTombstone(
  note: Pick<Note, 'lastModifiedDeviceId' | 'status'>,
): boolean {
  return note.status === 'trash'
    && note.lastModifiedDeviceId.startsWith(GOOGLE_KEEP_ROLLBACK_DEVICE_PREFIX)
}

export function inferredTagsForNote(
  note: Pick<Note, 'contentType' | 'suggestedTags' | 'urls'>,
): string[] {
  const categories = note.urls.map((url) => urlContentCategory(url, note.contentType))
  return [...new Set([
    ...note.suggestedTags,
    ...suggestedTagsForUrlCategories(categories),
  ])]
}

export interface ActiveDraft {
  key: 'active'
  noteId: string
  body: string
  annotation: string
  userTags: string[]
  assetIds: string[]
  suppressedUrlKeys?: string[]
  expiresAt: number | null
  createdAt: number
  updatedAt: number
}

export interface NoteRevision {
  id: string
  noteId: string
  snapshot: Note
  modifiedAt: number
  deviceId: string
  reason: 'edit' | 'remote-update' | 'restore' | 'expiry' | 'delete'
  clientSchemaVersion: number
}

export interface SyncOperation {
  id: string
  entityType: 'note' | 'global-settings' | 'device-settings'
  entityId: string
  revisionId: string
  operation: 'upsert' | 'delete'
  createdAt: number
  attemptCount: number
  nextAttemptAt: number
  lastErrorCode: string | null
}

export const DEFAULT_TAGS = ['read', 'watch', 'buy'] as const
export const DEFAULT_EXPIRY_DAYS = 90

const URL_PATTERN = /(?:https?|waze):\/\/[^\s<>()\[\]{}"']+/gi
const INLINE_MARKDOWN_LINK_PATTERN = /!?\[((?:\\.|[^\]\\\n])*)\]\([^()\s]+(?:\s+["'][^"']*["'])?\)/gi
export { removeKnownTrackingParameters, TRACKING_PARAMS } from '../utils/urlTracking'

const GENERIC_PATH_PARTS = new Set(['article', 'articles', 'blog', 'dp', 'item', 'p', 'post', 'posts', 'product', 'search', 'story', 'watch',
  // Route words that name a kind of page, not the page: they say nothing without the site (docs/link-notes/PLAN.md, phase 7).
  'c', 'e', 'i', 'products', 'category', 'collections', 'page', 'pages', 'profile', 'groups', 'details', 'detail', 'events', 'event', 'expose', 'home', 'index', 'join', 'login', 'private', 'public', 'reel', 'reels', 'share', 'status', 'view', 'tv'])

const UUID_PATH_PART_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function urlKey(url: UrlEntity): string {
  return removeKnownTrackingParameters(url.canonicalUrl ?? url.originalUrl, true)
}

/** True when a path segment is an opaque UUID rather than readable title text. */
export function isOpaqueUrlPathPart(part: string): boolean {
  try {
    return UUID_PATH_PART_PATTERN.test(decodeURIComponent(part))
  } catch {
    return false
  }
}

function humanizePathPart(part: string): string | null {
  try {
    if (isOpaqueUrlPathPart(part)) return null
    const decoded = decodeURIComponent(part)
      .replace(/\.(?:aspx?|html?|php)$/i, '')
      .replace(/[-_+]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    if (
      decoded.length < 3 ||
      GENERIC_PATH_PARTS.has(decoded.toLowerCase()) ||
      /^\d+$/.test(decoded) ||
      // A language folder (de-de, en_GB) or an opaque short id (b3HtF, E482522, 5ktUTi) is not a name.
      /^[a-z]{2}(?:[-_ ][a-z]{2,4})?$/i.test(decoded) ||
      (decoded.length >= 4 && decoded.length < 24 && /[a-z]/i.test(decoded) && decoded.split(' ').every((word) => /\d/.test(word))) ||
      /^[@!]/.test(decoded) || /^data=/i.test(decoded) ||
      /^[a-z]{2}\d{5,}$/i.test(decoded) ||
      /^[a-f\d]{12,}$/i.test(decoded) ||
      /^[a-z\d]{24,}$/i.test(part)
    ) {
      return null
    }
    return `${decoded.charAt(0).toUpperCase()}${decoded.slice(1)}`
  } catch {
    return null
  }
}

/** Reject page-shell and opaque identifier titles that cannot identify the saved item. */
export function isUsableUrlTitle(url: UrlEntity, value: string | null | undefined): boolean {
  const title = value?.trim()
  if (!title) return false
  const targetUrl = url.canonicalUrl ?? url.originalUrl
  return !isProviderShellLinkTitle(targetUrl, title)
}

/**
 * The path-derived title the app used before route words, ids, locale folders and Maps data blobs were rejected. Notes saved then hold it as the
 * generated label of a link (`[B3HtF](address)`), so it must still count as generated, not as something the owner wrote.
 */
const LEGACY_GENERIC_PATH_PARTS = new Set(['article', 'articles', 'blog', 'dp', 'item', 'p', 'post', 'posts', 'product', 'search', 'story', 'watch'])
function legacyHumanizePathPart(part: string): string | null {
  try {
    if (isOpaqueUrlPathPart(part)) return null
    const decoded = decodeURIComponent(part).replace(/\.(?:aspx?|html?|php)$/i, '').replace(/[-_+]+/g, ' ').replace(/\s+/g, ' ').trim()
    if (decoded.length < 3 || LEGACY_GENERIC_PATH_PARTS.has(decoded.toLowerCase()) || /^\d+$/.test(decoded) || /^[a-z]{2}\d{5,}$/i.test(decoded)
      || /^[a-f\d]{12,}$/i.test(decoded) || /^[a-z\d]{24,}$/i.test(part)) return null
    return `${decoded.charAt(0).toUpperCase()}${decoded.slice(1)}`
  } catch {
    return null
  }
}
export function legacyPathTitle(address: string): string | null {
  try {
    const title = new URL(address).pathname.split('/').filter(Boolean).reverse().map(legacyHumanizePathPart).find((part): part is string => Boolean(part))
    return title ? (title.length > 72 ? `${title.slice(0, 69).trimEnd()}…` : title) : null
  } catch {
    return null
  }
}

/** The name in a Google Maps place address (`/maps/place/Urban+Nation/@52.4,13.3/data=…`): the place's own name, never the coordinates or data blob after it. */
function mapPlaceName(domain: string, pathname: string): string | null {
  if (!(domain === 'google.com' || domain.endsWith('.google.com'))) return null
  const encoded = /^\/maps\/place\/([^/]+)/i.exec(pathname)?.[1]
  if (!encoded) return null
  try {
    const [first, second] = decodeURIComponent(encoded).replace(/\+/g, ' ').split(',').map((part) => part.replace(/\s+/g, ' ').trim())
    // "Museum Island, Berlin" keeps its city; a street or postcode after the name ("Gendarmenmarkt, 10117 Berlin") is address, not name.
    const name = first && second && !/\d/.test(second) ? `${first}, ${second}` : first
    return name && !/^[@!]|^-?\d+(?:\.\d+)?$/.test(name) ? name : null
  } catch {
    return null
  }
}

/** Sites whose addresses carry no readable name, but whose kind of page is known: "Airbnb stay" says more than a room number. */
const PROVIDER_PAGE_TITLES: ReadonlyArray<readonly [host: RegExp, path: RegExp, title: string]> = [
  [/(^|\.)airbnb\.[a-z.]+$/, /^\/rooms\//i, 'Airbnb stay'],
  [/^(?:chatgpt\.com|chat\.openai\.com)$/, /^\/(?:c|share)\//i, 'ChatGPT conversation'],
  [/^grok\.com$/, /^\/(?:c|share)\//i, 'Grok conversation'],
  [/^gemini\.google\.com$/, /^\/share\//i, 'Gemini conversation'],
  [/^share\.gemini\.google$/, /./, 'Gemini conversation'],
  [/(^|\.)immobilienscout24\.de$/, /^\/expose\//i, 'ImmoScout24 listing'],
  [/(^|\.)instagram\.com$/, /^\/(?:reels?|p|tv)\//i, 'Instagram post'],
  [/(^|\.)linkedin\.com$/, /^\/(?:posts|feed\/update)\//i, 'LinkedIn post'],
  [/^meetu\.ps$|(^|\.)meetup\.com$/, /^\/e\/|\/events\//i, 'Meetup event'],
  [/^play\.google\.com$/, /^\/store\/books\//i, 'Google Play book'],
  [/^keep\.google\.com$/, /./, 'Google Keep note'],
]

/**
 * A name for a Reddit link the Worker could not name (Reddit shows a bare page to what it takes for a bot): the question of
 * an Answers link, the subreddit or user, or where a post that has no title in its address lives. A post with a title in its
 * address is named from that by the usual rule.
 */
function redditLabel(address: string): string | null {
  const target = classifyRedditUrl(address)
  if (!target) return null
  switch (target.kind) {
    case 'answers': return target.query?.replace(/\s+/g, ' ').trim() || 'Reddit Answers'
    case 'subreddit': return `r/${target.subreddit}`
    case 'user': return `u/${target.user}`
    case 'share': return new URL(address).pathname.startsWith('/a/answers/') ? 'Reddit Answers' : target.subreddit ? `Post in r/${target.subreddit}` : 'Reddit post'
    case 'short': return 'Reddit post'
    case 'post': return target.slug ? null : `Post in r/${target.subreddit ?? 'reddit'}`
    case 'comment': return target.slug ? null : `Comment in r/${target.subreddit ?? 'reddit'}`
    default: return null
  }
}

function providerFallbackTitle(domain: string, pathname: string): string | null {
  return PROVIDER_PAGE_TITLES.find(([host, path]) => host.test(domain) && path.test(pathname))?.[2] ?? null
}

export function urlDisplayTitle(url: UrlEntity): string {
  // A title saved before the reader decoded every character reference still carries them (`f&uuml;r`).
  const enrichedTitle = url.title ? decodeHtmlEntities(url.title).trim() : undefined
  if (enrichedTitle && isUsableUrlTitle(url, enrichedTitle)) return enrichedTitle

  const domain = url.domain?.replace(/^www\./, '') ?? 'link'
  if (domain === 'youtu.be' || domain.endsWith('youtube.com')) return 'YouTube video'
  if (domain.endsWith('vimeo.com')) return 'Vimeo video'
  if (domain.endsWith('tiktok.com')) return 'TikTok video'
  if (domain === 'imdb.com' || domain.endsWith('imdb.com')) return 'IMDb title'
  if (domain === 'spotify.com' || domain.endsWith('spotify.com')) return 'Spotify track'

  try {
    const parsed = new URL(url.canonicalUrl ?? url.originalUrl)
    if (
      (domain === 'perplexity.ai' || domain.endsWith('.perplexity.ai'))
      && /^\/search\/[^/]+\/?$/i.test(parsed.pathname)
    ) return 'Perplexity search'
    const redditName = redditLabel(parsed.toString())
    if (redditName) return redditName
    const place = mapPlaceName(domain, parsed.pathname)
    if (place) return place
    const provider = providerFallbackTitle(domain, parsed.pathname)
    if (provider) return provider
    const pathTitle = parsed.pathname
      .split('/')
      .filter(Boolean)
      .reverse()
      .map(humanizePathPart)
      .find((part): part is string => Boolean(part))
    if (pathTitle) return pathTitle.length > 72 ? `${pathTitle.slice(0, 69).trimEnd()}…` : pathTitle
  } catch {
    // The URL was already validated during extraction; retain a useful domain fallback.
  }

  return `Link from ${domain}`
}

export function mergeUrlMetadata(urls: UrlEntity[], existingUrls: UrlEntity[] = [], timing?: { now: number; legacyAddedAt: number }): UrlEntity[] {
  const existingByKey = new Map(existingUrls.map((url) => [urlKey(url), url]))
  return urls.map((url) => {
    const existing = existingByKey.get(urlKey(url))
    return existing
      ? {
          ...url,
          addedAt: existing.addedAt ?? url.addedAt ?? timing?.legacyAddedAt,
          title: existing.title ?? null,
          description: existing.description ?? null,
          imageUrl: existing.imageUrl ?? null,
          durationSeconds: existing.durationSeconds ?? null,
          siteName: existing.siteName ?? null,
          author: existing.author ?? null,
          publishedAt: existing.publishedAt ?? null,
          fetchedAt: existing.fetchedAt ?? null,
          metadataPolicyVersion: existing.metadataPolicyVersion ?? null,
          metadataFetchAttemptCount: existing.metadataFetchAttemptCount ?? 0,
          metadataNextRetryAt: existing.metadataNextRetryAt ?? null,
          // What kind of page it is travels with the link: the note's card names a book a book, not a link.
          ...(existing.pageKind ? { pageKind: existing.pageKind } : {}),
          ...(existing.gist ? { gist: existing.gist } : {}),
          ...(existing.answers?.length ? { answers: existing.answers } : {}),
          ...(existing.display ? { display: existing.display } : {}),
          ...(existing.topic ? { topic: existing.topic } : {}),
          ...(typeof existing.pinnedAt === 'number' ? { pinnedAt: existing.pinnedAt } : {}),
        }
      : timing ? { ...url, addedAt: url.addedAt ?? timing.now } : url
  })
}

export function createBlankDraft(now = Date.now()): ActiveDraft {
  return {
    key: 'active',
    noteId: createId(),
    body: '',
    annotation: '',
    userTags: [],
    assetIds: [],
    expiresAt: null,
    createdAt: now,
    updatedAt: now,
  }
}

export function hasDraftContent(draft: ActiveDraft): boolean {
  return (
    draft.body.trim().length > 0
    || draft.annotation.trim().length > 0
    || (draft.assetIds?.length ?? 0) > 0
  )
}

/** A storage-only label for an image-only note is not authored card content. */
export function isGeneratedFallbackTitle(note: Pick<Note, 'assetIds' | 'body' | 'derivedTitle' | 'manualTitle'>): boolean {
  return note.manualTitle === null
    && note.body.trim().length === 0
    && note.assetIds.length > 0
    && note.derivedTitle === 'Image note'
}

export function deriveTitle(body: string, urls = extractUrls(body), hasImages = false): string {
  const meaningfulLine = body
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line.length > 0 && !/^[-*+]\s+\[[ xX]\]\s*$/.test(line))

  if (meaningfulLine) {
    const cleaned = meaningfulLine
      .replace(/^#{1,6}\s+/, '')
      .replace(/^[-*+]\s+(?:\[[ xX]\]\s+)?/, '')
      .replace(INLINE_MARKDOWN_LINK_PATTERN, '$1')
      .replace(/<(?:https?|waze):\/\/[^\s<>()\[\]{}"']+>/gi, '')
      .replace(/(?<!\\)[*~`]/g, '')
      .replace(/(?<![\w\\])_|_(?!\w)/g, '')
      .replace(/\\([\\!"#$%&'()*+,\-./:;<=>?@[\]^_`{|}~])/g, '$1')
      .replace(/^(?:https?|waze):\/\/\S+$/i, '')
      .trim()

    if (cleaned) {
      // Only a standalone link's generated label may yield to metadata. Authored
      // prose/headings that happen to equal a URL fallback remain authoritative.
      const link = /^\[((?:\\.|[^\]\\\n])+)\]\((https?:\/\/[^\s)]+)(?:\s+["'][^"']*["'])?\)$/i.exec(meaningfulLine)
      const firstUrl = urls[0]
      if (
        link && firstUrl
        && removeKnownTrackingParameters(link[2]!, true) === urlKey(firstUrl)
        && (cleaned === urlDisplayTitle({ ...firstUrl, title: null }) || cleaned === legacyPathTitle(firstUrl.canonicalUrl ?? firstUrl.originalUrl))
        && isUsableUrlTitle(firstUrl, firstUrl.title)
      ) return urlDisplayTitle(firstUrl)
      return cleaned.length > 72 ? `${cleaned.slice(0, 69).trimEnd()}…` : cleaned
    }
  }

  const firstUrl = urls[0]
  return firstUrl ? urlDisplayTitle(firstUrl) : hasImages ? 'Image note' : 'Untitled note'
}

function urlBearingText(text: string): string {
  // A fetched title can itself mention URLs. Only its Markdown destination is
  // an attachment; extracting the label would invent additional saved links.
  // Padded with spaces: links written back to back (`[a](https://x)[b](https://y)`) are two addresses, not one.
  return text.replace(INLINE_MARKDOWN_LINK_PATTERN, (link, label: string) =>
    ` ${link.slice(link.indexOf('[') + label.length + 3, -1).split(/\s/, 1)[0] ?? ''} `,
  )
}

export function countUrlOccurrences(text: string): number {
  return (urlBearingText(text).match(URL_PATTERN) ?? []).length
}

/**
 * Markdown that went through another converter escapes characters inside addresses (`utm\_source`, `Local-first\_software`, a closing `\]`
 * or `%5C%5D` after a host). A backslash is never part of an address, so it is removed; a bracket it left at the end goes with it.
 */
export function unescapeMarkdownUrl(raw: string): string {
  return raw.replace(/\\+|%5C/gi, '').replace(/(?:%5D|%5B|\]|\[)+$/i, '')
}

const hasEscapeResidue = (raw: string) => /\\|%5C/i.test(raw)

export function extractUrls(text: string): UrlEntity[] {
  const matches = urlBearingText(text).match(URL_PATTERN) ?? []
  const unique = new Map<string, UrlEntity>()
  // A bare site address left over from a mangled link label (`https://site.de\]`) is not a link of its own when the real link is there.
  const leftovers = new Set<string>()

  for (const rawMatch of matches) {
    const matchedUrl = unescapeMarkdownUrl(rawMatch).replace(/[.,;:!?]+$/, '')
    const originalUrl = removeKnownTrackingParameters(matchedUrl)
    try {
      const parsed = new URL(originalUrl)
      parsed.hash = ''
      const canonicalUrl = parsed.toString()
      if (hasEscapeResidue(rawMatch) && parsed.pathname === '/' && !parsed.search) leftovers.add(canonicalUrl)
      if (!unique.has(canonicalUrl)) {
        unique.set(canonicalUrl, {
          originalUrl,
          canonicalUrl,
          domain: parsed.hostname.toLowerCase(),
          title: null,
          description: null,
          imageUrl: null,
          durationSeconds: null,
          siteName: null,
          author: null,
          publishedAt: null,
          fetchedAt: null,
          metadataPolicyVersion: null,
          metadataFetchAttemptCount: 0,
          metadataNextRetryAt: null,
        })
      }
    } catch {
      if (!unique.has(originalUrl)) {
        unique.set(originalUrl, {
          originalUrl,
          canonicalUrl: null,
          domain: null,
          title: null,
          description: null,
          imageUrl: null,
          durationSeconds: null,
          siteName: null,
          author: null,
          publishedAt: null,
          fetchedAt: null,
          metadataPolicyVersion: null,
          metadataFetchAttemptCount: 0,
          metadataNextRetryAt: null,
        })
      }
    }
  }

  const found = [...unique.values()]
  return found.filter((url) => !(leftovers.has(url.canonicalUrl ?? url.originalUrl) && found.some((other) => other !== url && other.domain === url.domain)))
}

/**
 * Links saved before addresses were unescaped carry the converter's backslashes (`utm\_source`, `site.de%5C%5D`), so they never resolved and
 * could not open. This gives such a link its clean address (its details are fetched afresh) and drops the bare-site leftovers of a mangled label.
 */
export function repairEscapedUrls(urls: UrlEntity[]): UrlEntity[] {
  if (!urls.some((url) => hasEscapeResidue(url.originalUrl))) return urls
  const kept = urls.filter((url) => !hasEscapeResidue(url.originalUrl))
  const repaired = urls.filter((url) => hasEscapeResidue(url.originalUrl)).flatMap((url) => extractUrls(url.originalUrl).map((clean) => ({
    clean, addedAt: url.addedAt, leftover: clean.canonicalUrl !== null && new URL(clean.canonicalUrl).pathname === '/' && !new URL(clean.canonicalUrl).search,
  })))
  const result = [...kept]
  for (const { clean, addedAt, leftover } of repaired) {
    const key = urlKey(clean)
    if (result.some((url) => urlKey(url) === key)) continue
    if (leftover && [...kept, ...repaired.filter((other) => !other.leftover).map((other) => other.clean)].some((url) => url.domain === clean.domain)) continue
    result.push(addedAt === undefined ? clean : { ...clean, addedAt })
  }
  return result
}

export function attachmentUrlKey(url: UrlEntity): string {
  return url.canonicalUrl ?? url.originalUrl
}

export function attachmentUrlsForDraft(draft: Pick<ActiveDraft, 'body' | 'suppressedUrlKeys'>): UrlEntity[] {
  const suppressed = new Set(draft.suppressedUrlKeys ?? [])
  return extractUrls(draft.body).filter((url) => !suppressed.has(attachmentUrlKey(url)))
}

export function applyPastedUrlAttachmentChoice(
  draft: ActiveDraft,
  urls: readonly UrlEntity[],
  attach: boolean,
): ActiveDraft {
  const existing = new Set(extractUrls(draft.body).map(attachmentUrlKey))
  const suppressed = new Set(draft.suppressedUrlKeys ?? [])
  for (const url of urls) {
    const key = attachmentUrlKey(url)
    if (attach) suppressed.delete(key)
    else if (!existing.has(key)) suppressed.add(key)
  }
  return { ...draft, suppressedUrlKeys: [...suppressed] }
}

export function classifyContent(body: string, urls = extractUrls(body), hasImages = false): {
  contentType: ContentType
  suggestedTags: string[]
} {
  if (urls.length === 0) {
    return { contentType: hasImages ? 'image' : 'text', suggestedTags: [] }
  }

  const linkOnlyMarkdown = /^\s*\[(?:\\.|[^\]\\\n])+\]\((?:https?|waze):\/\/[^\s)]+(?:\s+["'][^"']*["'])?\)\s*$/i.test(body)
  const initialCategories = urls.map((url) => urlContentCategory(url))
  // Words like "price" in shared text (a rental listing, say) must not outvote a kind
  // the page declared for itself.
  const hasProductLanguage = !urls.some((url) => isPageKind(url.pageKind))
    && /\b(?:buy|price|deal|purchase|order)\b/i.test(body)
  const linkOnly = urls.length === 1 && (body.trim() === urls[0]?.originalUrl || linkOnlyMarkdown)
  // A bare link is only an article when something says so; otherwise it is just a link.
  const contentType: ContentType = initialCategories.includes('video')
    ? 'video'
    : initialCategories.includes('product') || hasProductLanguage
      ? 'product'
      : linkOnly && (initialCategories[0] === 'article' || initialCategories[0] === 'reference')
        ? 'article'
        : 'link'
  const categories: UrlContentCategory[] = urls.map((url) =>
    urlContentCategory(url, contentType))
  return {
    contentType,
    suggestedTags: suggestedTagsForUrlCategories(categories),
  }
}

export function noteFromDraft(
  draft: ActiveDraft,
  baseNote: Note | null,
  deviceId: string,
  now = Date.now(),
): Note {
  const presentUrlKeys = new Set(extractUrls(draft.body).map(attachmentUrlKey))
  const suppressedUrlKeys = [...new Set(draft.suppressedUrlKeys ?? [])]
    .filter((key) => presentUrlKeys.has(key))
  const urls = mergeUrlMetadata(attachmentUrlsForDraft(draft), baseNote?.urls, {
    now, legacyAddedAt: baseNote?.createdAt ?? draft.createdAt,
  })
  const assetIds = [...new Set(draft.assetIds ?? [])]
  const classification = classifyContent(draft.body, urls, assetIds.length > 0)
  return {
    id: draft.noteId,
    body: draft.body,
    manualTitle: baseNote?.manualTitle ?? null,
    derivedTitle: deriveTitle(draft.body, urls, assetIds.length > 0),
    annotation: draft.annotation,
    userTags: normalizeTags(draft.userTags),
    suggestedTags: classification.suggestedTags,
    contentType: classification.contentType,
    urls,
    suppressedUrlKeys,
    assetIds,
    status: baseNote?.status ?? 'inbox',
    quickAccess: baseNote?.quickAccess ?? false,
    quickAccessAt: baseNote?.quickAccessAt ?? null,
    reminder: baseNote?.reminder ?? null,
    snoozedUntil: baseNote?.snoozedUntil ?? null,
    expiresAt: draft.expiresAt,
    expiredAt: baseNote?.expiredAt ?? null,
    trashedAt: baseNote?.trashedAt ?? null,
    createdAt: baseNote?.createdAt ?? draft.createdAt,
    modifiedAt: now,
    capturedAt: baseNote?.capturedAt ?? draft.createdAt,
    originDeviceId: baseNote?.originDeviceId ?? deviceId,
    lastModifiedDeviceId: deviceId,
    revisionId: createId(),
    localSyncState: 'pending',
  }
}

export function draftFromNote(note: Note, now = Date.now()): ActiveDraft {
  return {
    key: 'active',
    noteId: note.id,
    body: note.body,
    annotation: note.annotation,
    userTags: [...note.userTags],
    assetIds: [...(note.assetIds ?? [])],
    suppressedUrlKeys: [...(note.suppressedUrlKeys ?? [])],
    expiresAt: note.expiresAt,
    createdAt: note.createdAt,
    updatedAt: now,
  }
}

export function normalizeTags(tags: string[]): string[] {
  return [...new Set(tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean))]
}

const INLINE_TAG_PATTERN = /(?:^|\s)#([a-z0-9][a-z0-9_-]*)(?![a-z0-9_-])/gi
const COMPLETED_INLINE_TAG_PATTERN = /(?:^|\s)#([a-z0-9][a-z0-9_-]*)\s+$/i

/** Extracts user-authored inline hashtags without matching Markdown headings or URL fragments. */
export function extractInlineTags(text: string): string[] {
  return normalizeTags([...text.matchAll(INLINE_TAG_PATTERN)].map((match) => match[1] ?? ''))
}

/** Returns the completed hashtag immediately before a collapsed editor caret. */
export function extractCompletedInlineTag(textBeforeCaret: string): string | null {
  const tag = COMPLETED_INLINE_TAG_PATTERN.exec(textBeforeCaret)?.[1]
  return tag ? normalizeTags([tag])[0] ?? null : null
}

export function expiryFromNow(days = DEFAULT_EXPIRY_DAYS, now = Date.now()): number {
  return now + days * 24 * 60 * 60 * 1000
}

export function transitionNoteStatus(
  note: Note,
  status: NoteStatus,
  deviceId: string,
  now = Date.now(),
): Note {
  const restoringExpired = note.status === 'expired' && status !== 'expired'
  return {
    ...note,
    status,
    quickAccess: status === 'trash' ? false : note.quickAccess,
    quickAccessAt: status === 'trash' ? null : note.quickAccessAt,
    modifiedAt: now,
    snoozedUntil: status === 'snoozed' ? now + 7 * 24 * 60 * 60 * 1000 : null,
    expiresAt: restoringExpired ? null : note.expiresAt,
    expiredAt: status === 'expired' ? now : restoringExpired ? null : note.expiredAt,
    trashedAt: status === 'trash' ? now : status === 'inbox' ? null : note.trashedAt,
    revisionId: createId(),
    lastModifiedDeviceId: deviceId,
    localSyncState: 'pending',
  }
}

export function setNoteQuickAccess(
  note: Note,
  quickAccess: boolean,
  deviceId: string,
  now = Date.now(),
): Note {
  return {
    ...bumpNote(note, deviceId, now),
    quickAccess,
    quickAccessAt: quickAccess ? now : null,
  }
}

/** A picture-only note in Quick Access takes its unpacked card's title, so the widget (and its server refresh) names the booking, not "Image note". */
export function withUnpackedTitle(note: Note, title: string | null | undefined): Note {
  const clean = title?.replace(/\s+/g, ' ').trim().slice(0, 180)
  return clean && isGeneratedFallbackTitle(note) ? { ...note, manualTitle: clean } : note
}

export function bumpNote(
  note: Note,
  deviceId: string,
  now = Date.now(),
): Note {
  return {
    ...note,
    modifiedAt: Math.max(now, note.modifiedAt + 1),
    revisionId: createId(),
    lastModifiedDeviceId: deviceId,
    localSyncState: 'pending',
  }
}

export function expireNoteIfDue(note: Note, deviceId: string, now = Date.now()): Note | null {
  if (
    note.expiresAt === null ||
    note.expiresAt > now ||
    note.status === 'expired' ||
    note.status === 'trash'
  ) {
    return null
  }
  return transitionNoteStatus(note, 'expired', deviceId, now)
}

export function compareNoteVersions(left: Note, right: Note): number {
  if (left.modifiedAt !== right.modifiedAt) return left.modifiedAt - right.modifiedAt
  const deviceOrder = left.lastModifiedDeviceId.localeCompare(right.lastModifiedDeviceId)
  if (deviceOrder !== 0) return deviceOrder
  return left.revisionId.localeCompare(right.revisionId)
}

export function formatRelativeTime(timestamp: number, now = Date.now()): string {
  const elapsedSeconds = Math.max(0, Math.round((now - timestamp) / 1000))
  if (elapsedSeconds < 10) return 'now'
  if (elapsedSeconds < 60) return `${elapsedSeconds}s`
  const minutes = Math.round(elapsedSeconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d`
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(timestamp)
}
